import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  Row,
  Col,
  Alert,
  Card,
  Spinner,
  Form,
  ListGroup,
  Badge,
} from 'react-bootstrap';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import availableTimeService, {
  AvailableTimesResponse,
  formatDateForAPI,
  getAvailableTimesForDate,
  getAvailableTimeObjectsForDate,
} from '../services/availableTimeService';
import SideMenu from '../components/common/SideMenu';
import AdminMenuButton from '../components/common/AdminMenuButton';
import './Common.scss';

const AvailableTimes: React.FC = () => {
  // 基本状態管理
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [isSideMenuOpen, setIsSideMenuOpen] = useState<boolean>(true);

  // カレンダーと時間管理
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [availableTimesData, setAvailableTimesData] =
    useState<AvailableTimesResponse>({});
  const [selectedDateTimes, setSelectedDateTimes] = useState<string[]>([]);
  const [timeListLoading, setTimeListLoading] = useState<boolean>(false);

  // 時間帯設定用
  const [isAddingTime, setIsAddingTime] = useState<boolean>(false);
  const [newTimeSlot, setNewTimeSlot] = useState<string>('');

  const handleSideMenuToggle = (isOpen: boolean): void => {
    setIsSideMenuOpen(isOpen);
  };

  // メッセージを自動で消去するヘルパー関数
  const clearMessagesAfterDelay = (): void => {
    setTimeout(() => {
      setError('');
      setSuccess('');
    }, 5000);
  };

  // 初期データ読み込み（件数表示用）
  const fetchAvailableTimesData = async (currentDate?: Date): Promise<void> => {
    setLoading(true);
    try {
      const data = await availableTimeService.getAvailableTimes(
        currentDate?.getFullYear() || selectedDate.getFullYear(),
        (currentDate?.getMonth() || selectedDate.getMonth()) + 1
      );
      setAvailableTimesData(data);
      setError('');
    } catch (err) {
      console.error('受付可能時間データ取得エラー:', err);
      setError('受付可能時間データの取得に失敗しました。');
      clearMessagesAfterDelay();
    } finally {
      setLoading(false);
    }
  };

  // 特定日付の時間帯を取得
  const fetchTimesForDate = async (date: Date): Promise<void> => {
    setTimeListLoading(true);
    try {
      const dateKey = formatDateForAPI(date);
      const timesForDate = getAvailableTimesForDate(
        availableTimesData,
        dateKey
      );
      setSelectedDateTimes(timesForDate);
    } catch (err) {
      console.error('指定日の時間帯取得エラー:', err);
      setError('指定日の時間帯取得に失敗しました。');
      clearMessagesAfterDelay();
    } finally {
      setTimeListLoading(false);
    }
  };

  // 新しい時間帯を追加
  const handleAddTimeSlot = async (): Promise<void> => {
    if (!newTimeSlot.trim()) {
      setError('時間を入力してください。');
      clearMessagesAfterDelay();
      return;
    }

    try {
      const dateKey = formatDateForAPI(selectedDate);
      const currentTimes = getAvailableTimesForDate(
        availableTimesData,
        dateKey
      );

      if (currentTimes.includes(newTimeSlot)) {
        setError('その時間は既に登録されています。');
        clearMessagesAfterDelay();
        return;
      }

      const response = await availableTimeService.createAvailableTime({
        date: dateKey,
        start_time: newTimeSlot,
      });

      // バックエンドからのレスポンスを使用してデータを更新
      if (response && response.start_time) {
        // サーバーから返された新しいAvailableTimeオブジェクトを使用
        const currentTimeObjects = getAvailableTimeObjectsForDate(
          availableTimesData,
          dateKey
        );
        const updatedTimeObjects = [...currentTimeObjects, response].sort(
          (a, b) => a.start_time.localeCompare(b.start_time)
        );
        const updatedData = {
          ...availableTimesData,
          [dateKey]: updatedTimeObjects,
        };

        setAvailableTimesData(updatedData);

        // 文字列配列も更新
        const updatedTimes = updatedTimeObjects.map(time => time.start_time);
        setSelectedDateTimes(updatedTimes);

        setNewTimeSlot('');
        setIsAddingTime(false);
        setSuccess('時間帯を追加しました。');
      } else {
        // レスポンスが期待した形式でない場合は全データを再取得
        await fetchAvailableTimesData(selectedDate);
        await fetchTimesForDate(selectedDate);
        setNewTimeSlot('');
        setIsAddingTime(false);
        setSuccess('時間帯を追加しました。');
      }
      clearMessagesAfterDelay();
    } catch (err) {
      console.error('時間帯追加エラー:', err);
      setError('時間帯の追加に失敗しました。');
      clearMessagesAfterDelay();
    }
  };

  // 時間帯を削除
  const handleDeleteTimeSlot = async (timeSlot: string): Promise<void> => {
    try {
      const dateKey = formatDateForAPI(selectedDate);

      // バックエンドAPIで時間帯を削除
      await availableTimeService.deleteAvailableTimeByDateTime(
        dateKey,
        timeSlot
      );

      // 削除成功後、データを再取得して整合性を保つ
      await fetchAvailableTimesData(selectedDate);
      await fetchTimesForDate(selectedDate);

      // 削除成功後、即座にUIを更新（楽観的更新）
      const updatedTimes = selectedDateTimes.filter(time => time !== timeSlot);
      setSelectedDateTimes(updatedTimes);

      setSuccess('時間帯を削除しました。');
      clearMessagesAfterDelay();
    } catch (err) {
      console.error('時間帯削除エラー:', err);
      setError('時間帯の削除に失敗しました。');
      clearMessagesAfterDelay();
    }
  };

  // カレンダーのタイル内容をカスタマイズ（件数表示）
  const tileContent = ({
    date: tileDate,
    view,
  }: {
    date: Date;
    view: string;
  }) => {
    if (view === 'month') {
      const dateKey = formatDateForAPI(tileDate);
      const timesForDate = getAvailableTimesForDate(
        availableTimesData,
        dateKey
      );
      const count = timesForDate.length;

      if (count > 0) {
        return (
          <div className="tile-count">
            <Badge bg="success" className="count-badge">
              {count}
            </Badge>
          </div>
        );
      }
    }
    return null;
  };

  // 日付選択時の処理
  const handleDateChange = (value: any) => {
    if (value instanceof Date) {
      const previousMonth = selectedDate.getMonth();
      const previousYear = selectedDate.getFullYear();
      const newMonth = value.getMonth();
      const newYear = value.getFullYear();

      setSelectedDate(value);

      // 月または年が変更された場合は利用可能時間データを再取得
      if (previousMonth !== newMonth || previousYear !== newYear) {
        fetchAvailableTimesData(value);
      } else {
        // 同じ月内での日付変更の場合は時間帯のみ取得
        fetchTimesForDate(value);
      }
    }
  };

  // 初期読み込み
  useEffect(() => {
    fetchAvailableTimesData();
  }, []);

  // 選択日付が変更されたときに時間帯を取得
  useEffect(() => {
    if (Object.keys(availableTimesData).length > 0) {
      fetchTimesForDate(selectedDate);
    }
  }, [selectedDate, availableTimesData]);

  return (
    <div className="page-layout .available-times-height">
      <AdminMenuButton
        onToggle={handleSideMenuToggle}
        isOpen={isSideMenuOpen}
      />

      <Container fluid>
        <Row className="min-vh-100 g-0">
          {/* サイドバー: 条件付き表示 */}
          {isSideMenuOpen && (
            <Col xs={12} sm={12} md={3} lg={3} className="admin-sidebar-col">
              <div className="p-3 position-sticky" style={{ top: '1rem' }}>
                <SideMenu />
              </div>
            </Col>
          )}

          {/* メインコンテンツ: サイドバーの状態に応じて幅を調整 */}
          <Col
            xs={12}
            sm={12}
            md={isSideMenuOpen ? 9 : 12}
            lg={isSideMenuOpen ? 9 : 12}
            className="py-4 admin-main-content"
          >
            {/* エラー・成功メッセージ */}
            {error && (
              <Row className="mb-3">
                <Col>
                  <Alert variant="danger" className="mb-0">
                    {error}
                  </Alert>
                </Col>
              </Row>
            )}

            {success && (
              <Row className="mb-3">
                <Col>
                  <Alert variant="success" className="mb-0">
                    {success}
                  </Alert>
                </Col>
              </Row>
            )}

            {loading ? (
              <Row className="justify-content-center">
                <Col xs="auto">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </Col>
              </Row>
            ) : (
              <Row>
                {/* カレンダー部分 */}
                <Col lg={6} md={12} className="mb-4">
                  <Card>
                    <Card.Header className="bg-success text-white">
                      <h5 className="mb-0">受付可能日カレンダー</h5>
                      <small>日付をクリックして時間帯を設定</small>
                    </Card.Header>
                    <Card.Body className="d-flex justify-content-center p-3">
                      <Calendar
                        onChange={handleDateChange}
                        value={selectedDate}
                        className="border-0"
                        tileContent={tileContent}
                        minDate={new Date()}
                      />
                    </Card.Body>
                  </Card>
                </Col>

                {/* 時間帯リスト部分 */}
                <Col lg={6} md={12}>
                  <Card>
                    <Card.Header className="bg-success text-white">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-0">
                            {selectedDate.toLocaleDateString('ja-JP', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </h5>
                          <small>受付可能時間一覧</small>
                        </div>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={() => setIsAddingTime(true)}
                          disabled={isAddingTime}
                        >
                          + 時間追加
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {/* 新しい時間帯追加フォーム */}
                      {isAddingTime && (
                        <Form className="mb-3 p-3 bg-light rounded">
                          <Row>
                            <Col sm={8}>
                              <Form.Control
                                type="time"
                                value={newTimeSlot}
                                onChange={e => setNewTimeSlot(e.target.value)}
                                placeholder="例: 10:00"
                              />
                            </Col>
                            <Col sm={4}>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={handleAddTimeSlot}
                                >
                                  追加
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => {
                                    setIsAddingTime(false);
                                    setNewTimeSlot('');
                                  }}
                                >
                                  キャンセル
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </Form>
                      )}

                      {/* 時間帯リスト */}
                      {timeListLoading ? (
                        <div className="text-center">
                          <Spinner animation="border" size="sm" />
                          <span className="ms-2">読み込み中...</span>
                        </div>
                      ) : selectedDateTimes.length > 0 ? (
                        <ListGroup>
                          {selectedDateTimes.map((timeSlot, index) => (
                            <ListGroup.Item
                              key={index}
                              className="d-flex justify-content-between align-items-center"
                            >
                              <span className="fs-5">{timeSlot}</span>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteTimeSlot(timeSlot)}
                              >
                                削除
                              </Button>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      ) : (
                        <div className="text-center text-muted py-4">
                          <p>この日に登録された時間帯はありません</p>
                          <Button
                            variant="outline-success"
                            onClick={() => setIsAddingTime(true)}
                            disabled={isAddingTime}
                          >
                            最初の時間帯を追加
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AvailableTimes;
